import { computed, defineComponent, h, onBeforeUnmount, onMounted, onUpdated, ref, VNode, watch, WatchStopHandle } from 'vue'
import { QBanner, QTabPanels, QTabPanel } from 'quasar'

import { BsBtn, BsForm } from '../index'
import { BsFormRef } from '../form/BsForm'
import { BsComponentRef, bsComponentProps, useBsComponent } from '../useBsComponent'
import { emitModel } from '../emitModel'
import { useVm } from '../../../composables/useVm'
import { BsTabDefinition } from '../types'
import { validStr } from 'booksprout'
import { pushUrlHash } from '../../../composables/useUrlFilters'
import { ComponentState } from '../useBsComponent'
import { childComponents } from '../childComponents'
import { useTemplateRef } from '../useTemplateRef'

/**
 * The wizard body: one QTabPanel per step, each wrapped in its own BsForm, plus the previous/next action
 * row underneath. Stepping forward validates the form of the step being left.
 *
 * On top of that it can guard a step's *required* fields — `guardRequiredFields`. QField's own validation
 * only fires on interaction, so a step the author never touched would pass; this walks the current form's
 * children instead, flags what is empty, and offers to scroll to the first one. Components can opt out of
 * the generic handling by implementing the five `requiredField*` hooks below.
 */

/**
 * A component the required-field guard tracks. Its `useBsComponent` members are what the guard reads and
 * writes; the optional hooks let a component answer for itself — CodesUploadChipBox does, because what
 * counts as filled there is not its own value.
 */
type RequiredGuardComponent = BsComponentRef & {
  componentValue?: any
  required?: boolean
  isComponentIsValid?: (val?: any) => boolean
  clearRequiredFieldError?: () => void
  isRequiredValueMissing?: () => boolean
  requiredFieldTrackingKey?: () => string
  scrollToRequiredField?: () => void
  setRequiredFieldError?: (message: string) => void
}

export default defineComponent({
  name: 'BsTabStepper',

  // Declared for the reason in BsInput: an undeclared event that shares a DOM event's name is bound
  // twice, once as a component event and once natively on the root, so the handler runs again with a
  // DOM event as its payload. `update:modelValue` rides along for `v-model`.
  emits: [ 'input', 'update:modelValue', 'done', 'return' ],

  props: {
    ...bsComponentProps,
    value: { type: Number, default: void 0 },
    tabs: {
      type: Array as unknown as () => BsTabDefinition[],
      default: void 0
    },
    pad: {
      type: Array as unknown as () => [],
      default: void 0
    },
    doneLabel: { type: String, default: void 0 },
    returnLabel: { type: String, default: void 0 },
    returnTo: {
      // eslint-disable-next-line @typescript-eslint/ban-types
      type: [String, Function] as unknown as () => string | Function,
      default: void 0
    },
    requiredFieldGuardSubject: { type: String, default: void 0 },
    // eslint-disable-next-line @typescript-eslint/ban-types
    doBeforeNext: { type: Function as unknown as () => Function, default: void 0 },
    /**
     * Declared WITHOUT defaults, as the class declared them: Vue's own casting makes an absent Boolean
     * false, and two of these are read as `=== false` / `!== false`, so a `default: void 0` would invert
     * them. (An absent `showRequiredFieldBanner` therefore means no banner — pinned by the spec.)
     */
    hideNext: { type: Boolean },
    hideActions: { type: Boolean },
    disableNext: { type: Boolean },
    useSlotKey: { type: Boolean },
    guardRequiredFields: { type: Boolean },
    showRequiredFieldBanner: { type: Boolean }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsForm, set: setBsFormRef } = useTemplateRef<any>()
    const { ref: bsStepperTabPanels, set: setBsStepperTabPanelsRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({ vm, props, emit, value: () => props.value })

    /**
     * The step this stepper is on, under either name.
     *
     * `props.value` alone is not enough: `v-model="step"` on a `bs-tab-stepper` compiles to
     * `:modelValue` under Vue 3, so the declared `value` prop is undefined and every step calculation
     * below fell out — `currentTab` came back undefined and `renderDefaultActions` returned NOTHING, so
     * the wizard rendered with no "Step 2" / "Step 3" buttons at all. `componentValue` resolves both
     * names (see useBsComponent).
     */
    const currentStep = computed<number>(() => bs.componentValue.value as number)

    const hasMissingRequiredFields = ref(false)

    /** Guards `scheduleRequiredFieldSync` against re-entering while a sync is already queued. */
    const isSyncingRequiredFields = ref(false)

    /**
     * The watchers this put on the tracked components, kept so they can be stopped.
     *
     * These were `component.$on('input', …)` listeners. Vue 3 removed `$on`/`$off` from the instance
     * -- "component.$on is not a function" out of the guard, which is an unhandled rejection and takes
     * the page with it -- so the same job is done by watching the value each component reports. It is
     * the closer expression of the intent anyway: the guard re-checks when a required field's value
     * changes, however the change reached it.
     */
    const requiredFieldListeners = ref<Array<{
      component: RequiredGuardComponent,
      stop: WatchStopHandle
    }>>([])

    const loadingWidth = computed(() => {
      const pc = (100 / props.tabs.length) * currentStep.value
      return `${pc}%`
    })

    const nextStep = computed(() =>
      currentStep.value < props.tabs.length ? currentStep.value + 1 : props.tabs.length)

    const previousStep = computed(() =>
      currentStep.value > 1 ? currentStep.value - 1 : currentStep.value)

    const hasBlockingRequiredFields = computed(() =>
      props.guardRequiredFields && hasMissingRequiredFields.value)

    const shouldShowRequiredFieldBanner = computed(() =>
      props.showRequiredFieldBanner !== false && hasBlockingRequiredFields.value)

    const requiredFieldGuardSubjectLabel = computed(() =>
      validStr(props.requiredFieldGuardSubject) ? props.requiredFieldGuardSubject : 'item')

    // The sync has to run through the instance: the spec and the guard both replace these methods, and
    // the hooks below fire long after setup has returned.
    const host = vm as typeof vm & {
      scheduleRequiredFieldSync: () => void
      clearRequiredFieldListeners: () => void
    }

    onMounted(() => {
      host.scheduleRequiredFieldSync()
    })

    onUpdated(() => {
      host.scheduleRequiredFieldSync()
    })

    onBeforeUnmount(() => {
      host.clearRequiredFieldListeners()
    })

    return {
      bsForm,
      setBsFormRef,
      bsStepperTabPanels,
      setBsStepperTabPanelsRef,
      ...bs,
      currentStep,
      hasMissingRequiredFields,
      isSyncingRequiredFields,
      requiredFieldListeners,
      loadingWidth,
      nextStep,
      previousStep,
      hasBlockingRequiredFields,
      shouldShowRequiredFieldBanner,
      requiredFieldGuardSubjectLabel
    }
  },

  methods: {
    getSlot (step: BsTabDefinition) {
      return this.useSlotKey ? this.$slots[step.slotKey] : this.$slots[this.currentStep]
    },

    /** Through the router's history, or the author's next click puts the url back a step (sc-28993). */
    updateUrl (step: number) {
      pushUrlHash(this.$router, step)
    },

    previous () {
      if (this.currentStep === this.previousStep) {
        if (this.returnTo !== void 0) {
          if (typeof this.returnTo === 'function') {
            this.returnTo()
          } else {
            this.$router.push(this.returnTo)
          }
        } else {
          this.$emit('return')
        }
      } else {
        this.updateUrl(this.previousStep)
        ;(this.bsStepperTabPanels as QTabPanels)?.previous()
      }
    },

    async validateForm () {
      const formValid = (await this.getCurrentFormRef()?.validateForm()) ?? true
      const requiredGuardValid = this.validateRequiredFieldGuard()

      return formValid && requiredGuardValid
    },

    submitForm () {
      void this.next().catch(() => {
        // do nothing
        // promise rejected, for example epub preview generation failed
      })
    },

    async next () {
      if (this.disableNext) {
        return
      }

      const formValid = (await this.getCurrentFormRef()?.validateForm()) ?? true
      if (!formValid) {
        return
      }

      if (this.guardRequiredFields && !this.validateRequiredFieldGuard()) {
        return
      }

      if (typeof this.doBeforeNext !== 'function' || await this.doBeforeNext()) {
        const finished = this.nextStep === this.currentStep
        if (finished) {
          this.$emit('done')
        } else {
          this.updateUrl(this.nextStep)
          ;(this.bsStepperTabPanels as QTabPanels)?.next()
        }
      }
    },

    getCurrentFormRef () {
      // One BsForm per panel, so the ref holds an array when several are rendered.
      const forms = Array.isArray(this.bsForm)
        ? (this.bsForm as BsFormRef[])
        : [(this.bsForm as BsFormRef | undefined)]

      return forms.find(form => this.getFormStepValue(form) === this.currentStep) || forms.find(form => form !== void 0)
    },

    getFormStepValue (form?: BsFormRef) {
      return (form?.$parent?.$options?.propsData as { name?: number } | undefined)?.name ||
        (form?.$parent?.$?.vnode?.props as { name?: number } | undefined)?.name
    },

    getTrackedRequiredComponents (
      components: RequiredGuardComponent[] = childComponents(this.getCurrentFormRef()) as unknown as RequiredGuardComponent[],
      seenExpressions = new Set<string>()
    ): RequiredGuardComponent[] {
      const requiredComponents: RequiredGuardComponent[] = []

      for (const component of components) {
        if (this.isTrackedRequiredComponent(component)) {
          const trackingKey = this.getRequiredComponentTrackingKey(component)

          if (!seenExpressions.has(trackingKey)) {
            seenExpressions.add(trackingKey)
            requiredComponents.push(component)
          }
        }

        const children = childComponents(component as any) as unknown as RequiredGuardComponent[]
        if (children.length > 0) {
          requiredComponents.push(...this.getTrackedRequiredComponents(children, seenExpressions))
        }
      }

      return requiredComponents
    },

    getMissingRequiredComponents (components: RequiredGuardComponent[] = this.getTrackedRequiredComponents()) {
      return components.filter(component => this.isMissingRequiredValue(component))
    },

    getComponentModelExpression (component: RequiredGuardComponent) {
      // The v-model expression is gone in Vue 3; the `dataModel` prop is what identifies a field now,
      // falling back to the attribute for a component that sets it directly.
      return (component as any).dataModel || component.$attrs?.['data-model']
    },

    getRequiredComponentTrackingKey (component: RequiredGuardComponent) {
      const customTrackingKey = typeof component.requiredFieldTrackingKey === 'function'
        ? component.requiredFieldTrackingKey()
        : void 0

      return this.getComponentModelExpression(component) || customTrackingKey || // `_uid` is gone; the internal instance carries the same monotonic id.
        `required-component-${(component as any).$?.uid}`
    },

    getComponentValue (component: RequiredGuardComponent) {
      if (component.componentValue !== void 0) {
        return component.componentValue
      }

      const vnodeModel = { value: component.$attrs?.value }

      if (vnodeModel?.value !== void 0) {
        return vnodeModel.value
      }

      return component.$attrs?.value
    },

    isMissingRequiredValue (component: RequiredGuardComponent) {
      if (typeof component.isRequiredValueMissing === 'function') {
        return component.isRequiredValueMissing()
      }

      const value = this.getComponentValue(component)

      if (typeof value === 'number' && isNaN(value)) {
        return true
      }

      if (Array.isArray(value)) {
        return value.length === 0
      }

      return typeof component.isComponentIsValid === 'function'
        ? !component.isComponentIsValid(value)
        : value === null || value === '' || value === void 0
    },

    hasRequiredFlag (component: RequiredGuardComponent) {
      const requiredAttr = component.$attrs?.required as boolean | string | undefined

      return component.required === true ||
        requiredAttr === '' ||
        requiredAttr === true ||
        requiredAttr === 'true'
    },

    isTrackedRequiredComponent (component: RequiredGuardComponent) {
      const disabled = component.$attrs?.disable as boolean | string | undefined

      return this.hasRequiredFlag(component) &&
        disabled !== true &&
        (
          (typeof component.validate === 'function' && this.getComponentModelExpression(component) !== void 0) ||
          typeof component.isRequiredValueMissing === 'function'
        ) &&
        disabled !== 'true'
    },

    setRequiredComponentError (component: RequiredGuardComponent, message: string) {
      if (typeof component.setRequiredFieldError === 'function') {
        component.setRequiredFieldError(message)
        return
      }

      component.componentState = ComponentState.Invalid
      component.errorMessage = message
    },

    clearRequiredComponentError (component: RequiredGuardComponent) {
      if (typeof component.clearRequiredFieldError === 'function') {
        component.clearRequiredFieldError()
      }
    },

    validateRequiredFieldGuard () {
      if (!this.guardRequiredFields) {
        return true
      }

      const requiredComponents = this.getTrackedRequiredComponents()
      const missingRequiredComponents = this.getMissingRequiredComponents(requiredComponents)

      this.hasMissingRequiredFields = missingRequiredComponents.length > 0

      for (const component of requiredComponents) {
        if (!missingRequiredComponents.includes(component)) {
          this.clearRequiredComponentError(component)
        }
      }

      for (const component of missingRequiredComponents) {
        this.setRequiredComponentError(component, this.$tc('system.labels.requiredField'))
      }

      return missingRequiredComponents.length === 0
    },

    clearRequiredFieldListeners () {
      for (const { stop } of this.requiredFieldListeners) {
        stop()
      }

      this.requiredFieldListeners = []
    },

    syncRequiredFieldState () {
      if (!this.guardRequiredFields) {
        this.hasMissingRequiredFields = false
        this.clearRequiredFieldListeners()
        return
      }

      const requiredComponents = this.getTrackedRequiredComponents()
      const missingRequiredComponents = this.getMissingRequiredComponents(requiredComponents)

      this.clearRequiredFieldListeners()

      for (const component of requiredComponents) {
        // The same condition the guard itself asks, so the watcher fires exactly when the answer to it
        // could have changed -- for a component with its own `isRequiredValueMissing` as well as for
        // one the guard reads a value off.
        const stop = watch(
          () => this.isMissingRequiredValue(component),
          () => this.scheduleRequiredFieldSync()
        )

        this.requiredFieldListeners.push({
          component,
          stop
        })
      }

      for (const component of requiredComponents) {
        if (!missingRequiredComponents.includes(component)) {
          this.clearRequiredComponentError(component)
        }
      }

      this.hasMissingRequiredFields = missingRequiredComponents.length > 0
    },

    scheduleRequiredFieldSync () {
      if (!this.guardRequiredFields || this.isSyncingRequiredFields) {
        return
      }

      this.isSyncingRequiredFields = true

      void this.$nextTick(() => {
        this.isSyncingRequiredFields = false
        this.syncRequiredFieldState()
      })
    },

    scrollToFirstMissingRequiredField () {
      const missingRequiredComponents = this.getMissingRequiredComponents()
      const firstMissingRequiredComponent = missingRequiredComponents[0]

      if (firstMissingRequiredComponent === void 0) {
        return
      }

      for (const component of missingRequiredComponents) {
        this.setRequiredComponentError(component, this.$tc('system.labels.requiredField'))
      }

      this.hasMissingRequiredFields = true

      void this.$nextTick(() => {
        const element = firstMissingRequiredComponent.$el as HTMLElement | undefined

        if (typeof firstMissingRequiredComponent.scrollToRequiredField === 'function') {
          firstMissingRequiredComponent.scrollToRequiredField()
        } else {
          element?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })

          if (typeof (firstMissingRequiredComponent as any).focus === 'function') {
            ;(firstMissingRequiredComponent as any).focus()
          } else {
            const focusTarget = element?.querySelector('input, textarea, button, [tabindex]') as HTMLElement | null
            focusTarget?.focus()
          }
        }
      })
    },

    renderLoadingBar () {
      return h('div', { class: 'bs-stepper__loading-bar' }, [
        h('div', {
          class: 'bs-stepper__loading-bar--progress',
          style: { width: this.loadingWidth }
        })
      ])
    },

    renderRequiredFieldBanner () {
      if (!this.shouldShowRequiredFieldBanner) {
        return void 0
      }

      return h(QBanner, {
        class: 'bs-stepper__required-banner bg-bs-r-l text-bs-b',
        rounded: true
      }, {
        default: () => [
          h('span', [
            this.$t('system.labels.requiredFieldBannerStart', [this.requiredFieldGuardSubjectLabel]).toString(),
            h(BsBtn, {
              class: 'text-bs-g',
              asLink: true,
              onClick: () => this.scrollToFirstMissingRequiredField()
            }, { default: () => this.$t('system.actions.updateIt').toString() }),
            this.$t('system.labels.requiredFieldBannerEnd').toString()
          ])
        ]
      })
    },

    renderContent () {
      const nodes = []
      for (const step of this.tabs) {
        // A slot function takes a props bag; these are rendered without one.
        const renderSlot = this.getSlot(step) as ((props?: any) => VNode[]) | undefined

        nodes.push(
          h(QTabPanel, {
            class: 'bs-stepper__content',
            name: step.value,
            disable: step.disable
          }, {
            default: () => [
              h(BsForm, {
                ref: this.setBsFormRef,
                onSubmit: () => this.submitForm()
              }, {
                default: () => [
                  typeof renderSlot === 'function' ? renderSlot() : void 0,
                  this.renderActions()
                ]
              })
            ]
          })
        )
      }

      return h(QTabPanels, {
        // Quasar 2's v-model prop and event; the bundle's own API stays `value` / `input`, and
        // `emitModel` sends both so a caller's `v-model` updates as well as its `@input` firing.
        modelValue: this.currentStep,
        ['onUpdate:modelValue']: (v: any) => emitModel(this, v),
        ref: this.setBsStepperTabPanelsRef
      }, {
        default: () => nodes
      })
    },

    renderDefaultActions () {
      const
        previousStep = this.previousStep,
        nextStep = this.nextStep,
        // casting below because we know it'll exist.
        currentTab = this.tabs.find(f => f.value === this.currentStep) as BsTabDefinition,
        hiddenCount = this.tabs.filter(f => f.hidden || f.disable).length,
        finished = nextStep === this.currentStep

      if (currentTab === void 0) {
        return void 0
      } else {
        // Default labels
        let previousStepLabel = hiddenCount > 0 ? (previousStep - hiddenCount).toString() : previousStep.toString()
        let nextStepLabel = hiddenCount > 0 ? (nextStep - hiddenCount).toString() : nextStep.toString()

        // Check for overrides
        if (validStr(currentTab.previousLabel)) {
          previousStepLabel = currentTab.previousLabel
        } else { // Prepend step
          previousStepLabel = `Step ${previousStepLabel}`
        }

        // Check for overrides
        if (validStr(currentTab.nextLabel)) {
          nextStepLabel = currentTab.nextLabel
        } else { // Prepend step
          nextStepLabel = `Step ${nextStepLabel}`
        }

        const nodes = []
        const finalPreviousLabel = previousStep === this.currentStep && this.returnLabel !== void 0 ? this.returnLabel : previousStepLabel
        nodes.push(h(BsBtn, {
          class: ['bs-stepper__actions-default--left text-bs-g', {
            'invisible': previousStep === this.currentStep && this.returnLabel === void 0
          }],
          flat: true,
          icon: 'app:arrow-left',
          // use a custom previous function if available, otherwise go back to previous tab.
          onClick: () => typeof currentTab.previousAction === 'function' ? currentTab.previousAction() : this.previous(),
          ['data-model']: `stepper_action_${finalPreviousLabel}`
        }, {
          default: () => [h('span', { class: 'on-right' }, finalPreviousLabel)]
        }))

        nodes.push(h('div', { class: 'bs-stepper__actions-hint' }, [
          currentTab.actionHint,
          this.slot(this, 'actionsHint')
        ]))

        if (!this.hideNext && !currentTab.hideNext) {
          // If we have a custom "next" click event, use it, otherwise fallback to type submit.
          // A flat handler now, spread into the props below; `void 0` spreads to nothing.
          const on = typeof currentTab.nextAction === 'function'
            ? {
              onClick: () => {
                currentTab.nextAction !== void 0 && currentTab.nextAction()
              }
            }
            : void 0

          const finalNextLabel = finished ? (this.doneLabel || 'Finish') : nextStepLabel
          nodes.push(h(BsBtn, {
            class: 'bs-stepper__actions-default--right text-bs-g',
            disable: this.shouldShowRequiredFieldBanner || this.disableNext,
            flat: true,
            type: typeof currentTab.nextAction === 'function' ? void 0 : 'submit',
            iconRight: finished ? void 0 : 'app:arrow-right-alt',
            ...on,
            ['data-model']: finalNextLabel
          }, {
            default: () => [h('span', { class: 'on-left' }, finalNextLabel)]
          }))
        }

        return [
          h('div', { class: 'row justify-between items-center' }, nodes)
        ]
      }
    },

    renderActions () {
      const useDefaultActions = this.$slots.actions === void 0 && this.hideActions === false
      return h('div', {
        class: ['bs-stepper__actions', {
          'bs-stepper__actions-default': useDefaultActions
        }]
      }, [
        useDefaultActions ? this.renderDefaultActions() : this.slot(this, 'actions')
      ])
    }
  },

  render () {
    return h('div', { class: 'bs-stepper' }, [
      this.renderLoadingBar(),
      h('div', {
        class: [{ 'q-pa-md': this.pad }]
      }, [
        this.slot(this, 'default'),
        this.slot(this, 'banner'),
        this.renderRequiredFieldBanner(),
        this.renderContent()
      ])
    ])
  }
})
